/**
 * 収録中のライブ映像（Issue #617 / #626）。補正サーバー（`live-correct`）が同じ origin（CloudFront の /live/*）で配る `/live/stream` を
 * WebCodecs（VideoDecoder）でデコードして canvas に描く。**入力画面が `<video>` に対して使うものだけを同じ顔で持つ**:
 * `currentTime`（読み・書き＝シーク）/ `duration` / `paused` / `play()` / `pause()` / `videoWidth` / `videoHeight` と、
 * イベント `loadedmetadata` / `loadeddata` / `timeupdate` / `play` / `pause` / `seeked`。PlaceOnVideo が切り抜く元は `drawSource`（canvas）。
 * これで StageView・PlaceOnVideo・OcgInput の時刻の読み書き（応答の video_ms など）は `<video>` と同じ書き方のまま動く。
 *
 * ストリームの形（synchro-client `core/live/server.py` と同じ。独自アプリもこれを読む）: [tag u8][pts_us u64 LE][size u32 LE][payload]
 *   tag 1 = キーフレーム（Annex-B。SPS / PPS 付き） / 3 = それ以外のフレーム / 4 = 情報（JSON。最初に 1 回）
 *   / 5 = 音（AAC の 1 フレーム。録画済みの field.stream だけが入れる。Issue #769）。ここは映像の種類以外を読み飛ばす
 * 録画済みのフィールド動画も同じ形（field.stream。synchro-client `core/live/field_stream.py`。キーフレームの目次 field.stream.idx から Range で途中から読める）。
 * pts_us は録画の開始からの µs ＝ 入力の video_ms（÷1000）。録画は `?rec=<鍵>`、座標の確認は `?raw=<受け口>`（QuadSetupEditor）、補正サーバーの今の絵は `?preview=1` / `?seat=`（components/LiveStream.js）。
 *
 * 動き方（url は `/live/stream?rec=<鍵>` など。下の `from` / `once` はそこに足す）:
 *   - 再生中（ライブの端）: url のまま。届いたそばからデコードして描く
 *   - 止める: 受信を切って最後の絵のまま。currentTime はその絵の時刻
 *   - 再生（止めた位置から）: `from=<ms>` を足す。サーバーが実時間で送る。端に追いついたらそのままライブ
 *   - シーク（止めているとき）: `from=<ms>&once=1` を足す。手前のキーフレームから送られ、その時刻のフレームだけ描く
 *   - `goLive()`: ライブの端へ
 * VideoDecoder は https のページ（CloudFront）なら Safari・Chrome・Edge にある。
 */
import { liveUrl } from '../liveBase';

export const TAG_KEY = 1;
export const TAG_DELTA = 3;
export const TAG_INFO = 4;
/** 音（AAC の 1 フレーム。payload は ADTS の頭なし。AudioSpecificConfig は情報の audio.description）。読み飛ばす */
export const TAG_AUDIO = 5;
const HEADER = 13;
/** timeupdate を出す間隔（ms）。React の state を毎フレーム書き換えない */
const TIMEUPDATE_MS = 250;
/** ライブの端から何秒以内なら「ライブ」とみなすか */
export const LIVE_EDGE_SEC = 1.5;

/** バイト列を少しずつ受け取り、メッセージ { tag, ptsUs, payload } を返す */
export class StreamParser {
  constructor() { this.buf = new Uint8Array(0); }

  push(chunk) {
    const next = new Uint8Array(this.buf.length + chunk.length);
    next.set(this.buf, 0);
    next.set(chunk, this.buf.length);
    this.buf = next;
    const out = [];
    let p = 0;
    while (this.buf.length - p >= HEADER) {
      const view = new DataView(this.buf.buffer, this.buf.byteOffset + p, HEADER);
      const tag = view.getUint8(0);
      const ptsUs = Number(view.getBigUint64(1, true));
      const size = view.getUint32(9, true);
      if (this.buf.length - p - HEADER < size) break;
      out.push({ tag, ptsUs, payload: this.buf.slice(p + HEADER, p + HEADER + size) });
      p += HEADER + size;
    }
    this.buf = this.buf.slice(p);
    return out;
  }
}

/** Annex-B のキーフレームの SPS から WebCodecs のコーデック文字列（avc1.PPCCLL）。見つからなければ null */
export function codecFromAnnexB(data) {
  for (let i = 0; i + 4 < data.length; i += 1) {
    const sc3 = data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 1;
    if (!sc3) continue;
    const nal = data[i + 3];
    if ((nal & 0x1f) === 7 && i + 6 < data.length) {
      const hex = (b) => b.toString(16).padStart(2, '0');
      return `avc1.${hex(data[i + 4])}${hex(data[i + 5])}${hex(data[i + 6])}`;
    }
  }
  return null;
}

/**
 * 入力画面のライブ映像の道（Issue #744 / #765）: 録画の鍵があればその録画（fx なら描く側のゲーム映像＝`fx=1`。時刻は下に描いた実写の pts なので
 * 切り替えても応答の video_ms の意味は変わらない）、無ければ補正サーバーの今の絵（preview。fx は無い）
 */
export function liveStreamPath({ rec = null, fx = false } = {}) {
  if (!rec) return '/live/stream?preview=1';
  return `/live/stream?rec=${encodeURIComponent(rec)}${fx ? '&fx=1' : ''}`;
}

/** 読み込み先の URL（from は ms。once は止めた状態のシーク） */
export function streamUrl(base, { fromMs = null, once = false } = {}) {
  const q = [];
  if (fromMs != null) q.push(`from=${Math.max(0, Math.round(fromMs))}`);
  if (once) q.push('once=1');
  if (!q.length) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${q.join('&')}`;   // base に ? があれば & でつなぐ（/live/stream?rec=<録画の鍵>。Issue #627）
}

export default class LiveVideo extends EventTarget {
  /**
   * @param {object} [opts]
   * @param {string} [opts.url] `/live/stream?rec=<鍵>` など
   * @param {Function} [opts.fetchImpl] テスト用
   * @param {Function} [opts.Decoder] VideoDecoder の差し替え（テスト用）
   * @param {HTMLCanvasElement} [opts.canvas]
   * @param {Function} [opts.now] 時計（テスト用。ms）
   * @param {boolean} [opts.sameOrigin] 本番に教わった補正サーバーの口（liveBase）を使わず、いつも同じ origin の /live を読む（Discord の Activity。Issue #687）
   */
  constructor({ url = '/live/stream', fetchImpl = null, Decoder = null, canvas = null, now = null, sameOrigin = false } = {}) {
    super();
    this.url = url;
    this.sameOrigin = sameOrigin;
    this.fetchImpl = fetchImpl ?? ((...a) => fetch(...a));
    this.Decoder = Decoder ?? (typeof VideoDecoder !== 'undefined' ? VideoDecoder : null);   // eslint-disable-line no-undef
    this.canvas = canvas ?? (typeof document !== 'undefined' ? document.createElement('canvas') : null);
    this.now = now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.supported = !!this.Decoder;
    this.videoWidth = 0;
    this.videoHeight = 0;
    this.readyState = 0;
    this._t = 0;                  // 最後に描いたフレームの時刻（秒）
    this._latest = 0;             // 届いた中でいちばん新しい時刻（秒）＝ duration
    this.paused = true;
    this.following = false;       // ライブの端を追っている
    this.error = null;
    this.info = null;
    this._session = 0;
    this._abort = null;
    this._decoder = null;
    this._lastUpdate = -Infinity;
  }

  get drawSource() { return this.canvas; }
  get currentTime() { return this._t; }
  set currentTime(sec) { this.seek(sec); }
  get duration() { return this._latest; }
  /** ライブの端から何秒遅れているか（再生中の目安） */
  get lag() { return Math.max(0, this._latest - this._t); }
  get atLiveEdge() { return !this.paused && this.lag <= LIVE_EDGE_SEC; }

  _emit(type) { this.dispatchEvent(new Event(type)); }

  _timeupdate(force = false) {
    const t = this.now();
    if (!force && t - this._lastUpdate < TIMEUPDATE_MS) return;
    this._lastUpdate = t;
    this._emit('timeupdate');
  }

  /** ライブの端から再生する（最初に 1 回呼ぶ） */
  start() { return this.goLive(); }

  /** 今の再生の様子（同じ録画の別の配信へ切り替えるときに引き継ぐ。resume に渡す） */
  snapshot() { return { sec: this._t, paused: this.paused, following: this.following }; }

  /**
   * 前の配信の様子から始める（実写 ⇔ 演出の切り替え。Issue #765）: 端を追っていたら端から、止めていたらその時刻で止めたまま、
   * 途中を再生していたらその時刻から再生。無ければ start と同じ
   */
  resume(prev = null) {
    if (!prev || prev.following) return this.goLive();
    if (prev.paused) return this.seek(prev.sec);
    this._t = Math.max(0, Number(prev.sec) || 0);
    return this.play();
  }

  goLive() {
    const was = this.paused;
    this.paused = false;
    this.following = true;
    if (was) this._emit('play');
    return this._open({ fromMs: null, once: false, targetUs: null });
  }

  play() {
    if (!this.paused) return Promise.resolve();
    this.paused = false;
    this.following = false;
    this._emit('play');
    // 止めた位置の次のフレームから（止めた絵をもう一度描かない）
    return this._open({ fromMs: this._t * 1000, once: false, targetUs: Math.round(this._t * 1e6) + 1 }).then(() => undefined);
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.following = false;
    this._close();
    this._emit('pause');
    this._timeupdate(true);
  }

  seek(sec) {
    const target = Math.max(0, Number(sec) || 0);
    this.following = false;
    this._t = target;         // 読み手（±秒の連打）がすぐ新しい位置から数えられるように先に入れる
    return this._open({ fromMs: target * 1000, once: this.paused, targetUs: Math.round(target * 1e6), seeking: true });
  }

  destroy() {
    this._close();
    this._session += 1;
  }

  _close() {
    this._abort?.abort();
    this._abort = null;
    if (this._decoder && this._decoder.state !== 'closed') {
      try { this._decoder.close(); } catch (e) { /* 閉じ済み */ }
    }
    this._decoder = null;
  }

  _makeDecoder(session, targetUs, onFirst) {
    let first = true;
    const decoder = new this.Decoder({
      output: (frame) => {
        if (session !== this._session || frame.timestamp < targetUs) { frame.close(); return; }
        this._draw(frame);
        this._t = frame.timestamp / 1e6;
        if (this._t > this._latest) this._latest = this._t;
        frame.close();
        if (first) { first = false; onFirst?.(); }
        this._timeupdate();
      },
      error: (e) => { if (session === this._session) { this.error = e; this._emit('error'); } },
    });
    return decoder;
  }

  _draw(frame) {
    const c = this.canvas;
    if (!c) return;
    const w = frame.displayWidth; const h = frame.displayHeight;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    const firstMeta = this.videoWidth !== w || this.videoHeight !== h;
    this.videoWidth = w; this.videoHeight = h;
    c.getContext('2d')?.drawImage(frame, 0, 0);
    if (firstMeta) this._emit('loadedmetadata');
    if (this.readyState < 2) { this.readyState = 2; this._emit('loadeddata'); }
  }

  /** 1 本の受信。前の受信は切る。once なら最後まで読んでデコーダーを流しきる */
  async _open({ fromMs, once, targetUs, seeking = false }) {
    this._close();
    if (!this.supported) { this.error = new Error('この端末では VideoDecoder が使えません'); this._emit('error'); return; }
    this._session += 1;
    const session = this._session;
    const abort = new AbortController();
    this._abort = abort;
    let seekedSent = !seeking;
    const onFirst = () => { if (!seekedSent) { seekedSent = true; this._emit('seeked'); this._timeupdate(true); } };
    const decoder = this._makeDecoder(session, targetUs ?? -Infinity, onFirst);
    this._decoder = decoder;
    let configured = false;
    const parser = new StreamParser();
    try {
      const target = streamUrl(this.url, { fromMs, once });
      const res = await this.fetchImpl(this.sameOrigin ? target : liveUrl(target), { signal: abort.signal, cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      for (;;) {
        const { value, done } = await reader.read();
        if (done || session !== this._session) break;
        for (const m of parser.push(value)) {
          if (m.tag === TAG_INFO) {
            try { this.info = JSON.parse(new TextDecoder().decode(m.payload)); } catch (e) { this.info = null; }
            if (this.info?.latest_ms != null) this._latest = Math.max(this._latest, this.info.latest_ms / 1000);
            continue;
          }
          if (m.tag !== TAG_KEY && m.tag !== TAG_DELTA) continue;
          const key = m.tag === TAG_KEY;
          if (!configured) {
            if (!key) continue;
            decoder.configure({ codec: this.info?.codec ?? codecFromAnnexB(m.payload) ?? 'avc1.640032', optimizeForLatency: true });
            configured = true;
          }
          if (m.ptsUs / 1e6 > this._latest) this._latest = m.ptsUs / 1e6;
          // eslint-disable-next-line no-undef
          const Chunk = typeof EncodedVideoChunk !== 'undefined' ? EncodedVideoChunk : null;
          const chunk = Chunk ? new Chunk({ type: key ? 'key' : 'delta', timestamp: m.ptsUs, data: m.payload }) : { type: key ? 'key' : 'delta', timestamp: m.ptsUs, data: m.payload };
          decoder.decode(chunk);
        }
      }
      if (session === this._session && configured && decoder.state === 'configured') await decoder.flush();
    } catch (e) {
      if (e?.name === 'AbortError' || session !== this._session) return;
      this.error = e;
      this._emit('error');
    }
    if (session !== this._session) return;
    if (!seekedSent) { seekedSent = true; this._emit('seeked'); this._timeupdate(true); }
    // 再生中に切れた（補正サーバーを起動し直した・回線）→ 少し待って今の位置から繋ぎ直す
    if (!once && !this.paused) {
      setTimeout(() => {
        if (session !== this._session || this.paused) return;
        if (this.following) this.goLiveAgain(); else this._open({ fromMs: this._t * 1000, once: false, targetUs: Math.round(this._t * 1e6) + 1 });
      }, 1000);
    }
  }

  goLiveAgain() { return this._open({ fromMs: null, once: false, targetUs: null }); }
}

